import React, { FC, useContext, useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { Stack, Text, Box, Space } from '@tonic-ui/react';
import { useTranslation } from 'react-i18next';

import messageFilter from '../../utils/message-filter';
import useQueryParams from '../../utils/query-params';
import { AuthContext } from '../../context/AuthContext';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import {
  FormContainer,
  InputField,
  resetPasswordValidation,
  SubmitButton,
} from '../../components/forms';
import RouteLink from '../../components/route-link';
import CenteredLayout from '../../components/centered-layout';

interface ResetPasswordProps {
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: FC<ResetPasswordProps> = () => {
  const { setCognitoUser, setAuthStatus, eventDistributor } = useContext(AuthContext);
  const [formErrors, setFormErrors] = useState({
    error: '',
  });

  const { t } = useTranslation();
  const { email = '', code = '' } = useQueryParams<AuthQueryParams>(window.location);

  const handleSubmit = async ({ newPassword, setSubmitting, resetForm }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    try {
      // Confirm new password
      await Auth.forgotPasswordSubmit(email, code, newPassword);
      // Log user in
      const authenticatedUser = await Auth.signIn(email, newPassword);
      const { challengeName } = authenticatedUser;
      // Checkout Auth Type
      if (challengeName === 'SOFTWARE_TOKEN_MFA' || challengeName === 'SMS_MFA') {
        setCognitoUser(authenticatedUser);
        return eventDistributor.dispatch({
          type: 'NAVIGATE_APP',
          appName: 'mfa-login',
        });
      }

      setAuthStatus(authenticatedUser);
    } catch (error) {
      const errMsg = !error.message ? error : error.message;
      console.error('Error: ', errMsg);

      setFormErrors({
        ...formErrors,
        error: messageFilter(error),
      });

      setSubmitting(false);
      resetForm();
    }
  };

  const defaultValues: ResetPasswordProps = {
    newPassword: '',
    confirmPassword: '',
  };

  return (
    <CenteredLayout>
      <FormContainer
        id="reset-password-form"
        customValidation={resetPasswordValidation}
        initialValues={defaultValues}
        submitAction={(formPayload) => handleSubmit(formPayload)}
      >
        <Stack spacing="4x">
          <Text size="xl" lineHeight="xl">
            {t('passwordReset.newPasswordTitle')}
          </Text>
          {formErrors?.error && (
            <NotificationBanner
              name="reset-password-form"
              severity="error"
              variant="outline"
              shouldOpen={true}
              bannerText={formErrors.error}
            />
          )}
          <Box>
            <InputField
              id="new-password"
              type="password"
              label={t('passwordReset.newPassword')}
              name="newPassword"
              isRequired={true}
            />
          </Box>
          <Box>
            <InputField
              id="confirm-password"
              type="password"
              label={t('passwordReset.confirmPassword')}
              name="confirmPassword"
              isRequired={true}
            />
          </Box>
          <SubmitButton
            label={t('passwordReset.newPasswordSubmit')}
            id="reset-password-submit-btn"
          />
          <Box textAlign="center" mt={22}>
            <RouteLink href="/">{t('goBack')}</RouteLink>
          </Box>
        </Stack>
      </FormContainer>
    </CenteredLayout>
  );
};

export default ResetPassword;
